import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { SEO } from '../components/shared/SEO';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { MobileFooterNav } from '../components/layout/MobileFooterNav';
import { PharmacistChat } from '../components/services/PharmacistChat';
import { AnnouncementBar } from '../components/layout/AnnouncementBar';
import { MobileDeliverToPill } from '../components/layout/MobileDeliverToPill';
import { MobileInternalHeader } from '../components/layout/MobileInternalHeader';
import { LocationModal } from '../components/modals/LocationModal';
import { BrandShopByCategory } from '../components/brand-store/BrandShopByCategory';
import { BrandFeaturedProducts } from '../components/brand-store/BrandFeaturedProducts';
import { BrandOffers } from '../components/brand-store/BrandOffers';
import { BrandTopRated } from '../components/brand-store/BrandTopRated';
import { BrandComboSets } from '../components/brand-store/BrandComboSets';
import { BrandVideos } from '../components/brand-store/BrandVideos';
import { BrandQuickActions } from '../components/brand-store/BrandQuickActions';
import { BrandTimeline } from '../components/brand-store/BrandTimeline';
import { PageHeroBreadcrumb } from '../components/layout/PageHeroBreadcrumb';
import { PullToRefresh } from '../components/shared/PullToRefresh';
import { toast } from 'sonner';
import { StarIcon, CheckCircleIcon, TruckIcon, ShieldCheckIcon, HeartIcon } from 'lucide-react';
const sectionTabs = [{
  id: 'featured',
  label: 'Featured'
}, {
  id: 'categories',
  label: 'Categories'
}, {
  id: 'offers',
  label: 'Offers'
}, {
  id: 'top-rated',
  label: 'Top Rated'
}, {
  id: 'combos',
  label: 'Combos'
}, {
  id: 'videos',
  label: 'Videos'
}, {
  id: 'about',
  label: 'About'
}];
export function BrandStorePage() {
  const {
    brandSlug
  } = useParams<{
    brandSlug: string;
  }>();
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{
    name: string;
  } | null>(null);
  const [following, setFollowing] = useState(false);
  const [activeTab, setActiveTab] = useState('featured');
  const [showStickyHeader, setShowStickyHeader] = useState(false);
  const brandName = brandSlug?.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') || 'Brand';
  const brandData = {
    name: brandName,
    logo: 'https://images.unsplash.com/photo-1556228578-0d85b1a4d571?w=400',
    description: 'Premium skincare powered by thermal spa water and advanced dermatological science',
    since: '1931',
    tagline: 'Dermatological Beauty',
    stats: [{
      label: 'Products',
      value: '150+'
    }, {
      label: 'Countries',
      value: '50+'
    }, {
      label: 'Rating',
      value: '4.8/5'
    }, {
      label: 'Reviews',
      value: '25K+'
    }],
    features: [{
      icon: <CheckCircleIcon className="w-4 h-4" data-id="element-6249" data-mp="1f1a2exb95wp4" />,
      text: '100% Authentic'
    }, {
      icon: <TruckIcon className="w-4 h-4" data-id="element-6250" data-mp="1a26y5f33jui7" />,
      text: 'Fast Delivery'
    }, {
      icon: <ShieldCheckIcon className="w-4 h-4" data-id="element-6251" data-mp="26sg6tpy0agk7" />,
      text: 'Quality Guaranteed'
    }, {
      icon: <StarIcon className="w-4 h-4" data-id="element-6252" data-mp="22r4ehcnqzh8b" />,
      text: 'Dermatologist Recommended'
    }]
  };
  const handleTabClick = (id: string) => {
    setActiveTab(id);
    const el = document.getElementById(`brand-section-${id}`);
    if (!el) return;
    const offset = window.innerWidth >= 768 ? 240 : 170;
    const top = el.getBoundingClientRect().top + window.pageYOffset - offset;
    window.scrollTo({
      top,
      behavior: 'smooth'
    });
  };
  const handleFollow = () => {
    setFollowing(v => {
      const next = !v;
      toast.success(next ? `Following ${brandData.name}` : `Unfollowed ${brandData.name}`);
      return next;
    });
  };
  // Track scroll for sticky compact header + active tab
  useEffect(() => {
    const onScroll = () => {
      setShowStickyHeader(window.scrollY > 320);
      const viewportMid = window.innerHeight / 2;
      let current = 'featured';
      for (const t of sectionTabs) {
        const el = document.getElementById(`brand-section-${t.id}`);
        if (!el) continue;
        const r = el.getBoundingClientRect();
        if (r.top <= viewportMid && r.bottom >= viewportMid) {
          current = t.id;
          break;
        }
      }
      setActiveTab(current);
    };
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return <HelmetProvider data-id="element-6253" data-mp="3cnstrgn0s75h">
      <SEO title={`${brandName} Products`} description={`Shop authentic ${brandName} products at Innova Pharmacy. Fast delivery across Saudi Arabia with secure checkout and 24/7 support.`} canonical={`/en/brand/${brandSlug}/`} data-id="element-6254" data-mp="3coazd3vknxq2" />
      <div className="flex flex-col min-h-screen bg-gray-50" data-id="element-6255" data-mp="3hywv4spjaxu9">
        {/* Desktop Header */}
        <div className="hidden md:block" data-id="element-6256" data-mp="3vuifunoqivgv">
          <AnnouncementBar data-id="element-6257" data-mp="3qst5la9c6ltu" />
          <MobileDeliverToPill selectedLocation={selectedLocation} onOpenLocationModal={() => setLocationModalOpen(true)} data-id="element-6258" data-mp="3qumb2m87r009" />
          <Header locationModalOpen={locationModalOpen} setLocationModalOpen={setLocationModalOpen} selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} data-id="element-6259" data-mp="2z2ewdrc9pxoi" />
        </div>
        <main className="flex-grow pt-14 md:pt-[var(--header-height-desktop)]" data-id="element-6260" data-mp="3818q99y7s9yd">
          {/* Mobile Header Overlay */}
          <div className="md:hidden fixed top-0 left-0 right-0 z-50" data-id="element-6261" data-mp="sds2touqji5o">
            <MobileInternalHeader title={brandData.name} data-id="element-6262" data-mp="nc2tcdn5vcjp" />
          </div>

          {/* Mobile Sticky Compact Brand Header — appears on scroll */}
          <div className={`md:hidden fixed top-[56px] left-0 right-0 z-40 bg-white/95 backdrop-blur border-b border-gray-200 shadow-sm transition-transform duration-200 ${showStickyHeader ? 'translate-y-0' : '-translate-y-full'}`} data-id="element-6263" data-mp="zelxfjtggtt2">
            <div className="flex items-center justify-between px-4 py-2.5" data-id="element-6264" data-mp="19cjmg9j52umq">
              <div className="flex items-center gap-2.5 min-w-0" data-id="element-6265" data-mp="bqcfiszd4q77">
                <div className="w-9 h-9 rounded-lg bg-white border border-gray-200 overflow-hidden flex-shrink-0" data-id="element-6266" data-mp="go4jilda82rr">
                  <img src={brandData.logo} alt={brandData.name} className="w-full h-full object-contain" data-id="element-6267" data-mp="ln7e1bvmyqlh" />
                </div>
                <div className="min-w-0" data-id="element-6268" data-mp="3p4z9j08zdsof">
                  <p className="text-sm font-bold text-gray-900 truncate" data-id="element-6269" data-mp="3p23f3lwcdm4q">
                    {brandData.name}
                  </p>
                  <p className="text-[11px] text-gray-500 truncate" data-id="element-6270" data-mp="3mjht84cuvm55">
                    Since {brandData.since} • {brandData.stats[2].value} ★
                  </p>
                </div>
              </div>
              <button onClick={handleFollow} className={`flex-shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${following ? 'bg-gray-100 text-gray-700' : 'bg-[#1E2B55] text-white'}`} data-id="element-6271" data-mp="3cl2c8u7jlt9d">
                <HeartIcon className={`w-3.5 h-3.5 ${following ? 'fill-red-500 text-red-500' : ''}`} data-id="element-6272" data-mp="2yu70na5nsy23" />
                {following ? 'Following' : 'Follow'}
              </button>
            </div>
          </div>

          <h1 className="sr-only" data-id="element-6273" data-mp="2ox2fndnjv8mv">{brandName} - Official Products</h1>

          <PageHeroBreadcrumb breadcrumbs={[{
          label: 'Home',
          href: '/'
        }, {
          label: 'Brands',
          href: '/brands'
        }, {
          label: brandName
        }]} title={brandName} description={brandData.description} data-id="element-6274" data-mp="2jxfyit4lp88s" />

          {/* Brand Hero — Compact on mobile */}
          <div className="bg-gradient-to-r from-[#1E2B55] to-[#2a3a6f] text-white py-5 md:py-16" data-id="element-6275" data-mp="3tw7ce5j3czma">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-6276" data-mp="2pcznaf0y6pd">
              {/* Mobile compact layout */}
              <div className="md:hidden" data-id="element-6277" data-mp="3ps9dnvxohqtr">
                <div className="flex items-center gap-3 mb-4" data-id="element-6278" data-mp="3pt19n7v6tqkk">
                  <div className="bg-white rounded-xl p-2 shadow-lg flex-shrink-0" data-id="element-6279" data-mp="3b3lme1uv839m">
                    <img src={brandData.logo} alt={brandData.name} className="w-16 h-16 object-contain" data-id="element-6280" data-mp="3g2g1asppp7og" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-6281" data-mp="3640lhlw4xku9">
                    <div className="inline-block bg-white/20 px-2 py-0.5 rounded-full text-[10px] font-medium mb-1" data-id="element-6282" data-mp="363h2s2ynusf6">
                      Since {brandData.since}
                    </div>
                    <h2 className="text-lg font-bold leading-tight truncate" data-id="element-6283" data-mp="2iig5nf54kh6u">
                      {brandData.name}
                    </h2>
                    <p className="text-xs text-blue-100 truncate" data-id="element-6284" data-mp="2rhjp4k87eylv">
                      {brandData.tagline}
                    </p>
                  </div>
                  <button onClick={handleFollow} className={`flex-shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${following ? 'bg-white/20 text-white border border-white/30' : 'bg-[#fdc300] text-[#1E2B55]'}`} data-id="element-6285" data-mp="36d9fsvoc3zwx">
                    <HeartIcon className={`w-3.5 h-3.5 ${following ? 'fill-white' : ''}`} data-id="element-6286" data-mp="3b6c8l1laf5c2" />
                    {following ? 'Following' : 'Follow'}
                  </button>
                </div>
                {/* Stats row */}
                <div className="grid grid-cols-4 gap-2 mb-3" data-id="element-6287" data-mp="29k0v7qhpmlsq">
                  {brandData.stats.map((stat, index) => <div key={index} className="text-center bg-white/10 rounded-lg py-2" data-id="element-6288" data-mp="24r7qh2m8rdgn" data-mpi={index}>
                      <div className="text-base font-bold" data-id="element-6289" data-mp="1vslyyhysko3q">{stat.value}</div>
                      <div className="text-[10px] text-blue-200" data-id="element-6290" data-mp="2shtm1uf2759r">
                        {stat.label}
                      </div>
                    </div>)}
                </div>
                {/* Features as horizontal pills */}
                <div className="-mx-4 px-4" data-id="element-6291" data-mp="2jfu2ovgknp3n">
                  <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1" data-id="element-6292" data-mp="2eianntc1v8an">
                    {brandData.features.map((feature, index) => <div key={index} className="flex-shrink-0 flex items-center gap-1.5 bg-white/15 px-3 py-1.5 rounded-full text-[11px] whitespace-nowrap" data-id="element-6293" data-mp="183tbzb05dql9" data-mpi={index}>
                        {feature.icon}
                        <span data-id="element-6294" data-mp="xxib73bechdd">{feature.text}</span>
                      </div>)}
                  </div>
                </div>
              </div>

              {/* Desktop layout (unchanged) */}
              <div className="hidden md:flex flex-col md:flex-row items-center gap-6 md:gap-12" data-id="element-6295" data-mp="17p64zavfeai0">
                <div className="bg-white rounded-2xl p-6 md:p-8 shadow-xl" data-id="element-6296" data-mp="1booi3kh09haa">
                  <img src={brandData.logo} alt={brandData.name} className="w-32 h-32 md:w-48 md:h-48 object-contain" data-id="element-6297" data-mp="1go9q0ke5kzz5" />
                </div>
                <div className="flex-1 text-center md:text-left" data-id="element-6298" data-mp="1qlxonlevqrml">
                  <div className="inline-block bg-white bg-opacity-20 px-4 py-2 rounded-full text-sm font-medium mb-4" data-id="element-6299" data-mp="3nfimg97mt6tv">
                    Since {brandData.since}
                  </div>
                  <h2 className="text-3xl md:text-5xl font-bold mb-3" data-id="element-6300" data-mp="3kdnx5zyk1tyg">
                    {brandData.name}
                  </h2>
                  <p className="text-lg md:text-xl text-blue-100 mb-2" data-id="element-6301" data-mp="3ffco3rg33nha">
                    {brandData.tagline}
                  </p>
                  <p className="text-sm md:text-base text-blue-200 mb-6 max-w-2xl" data-id="element-6302" data-mp="3udeq3x7gw0wf">
                    {brandData.description}
                  </p>
                  <div className="grid grid-cols-4 gap-4 md:gap-6 mb-6" data-id="element-6303" data-mp="76uhsabjnqc9">
                    {brandData.stats.map((stat, index) => <div key={index} className="text-center" data-id="element-6304" data-mp="279ly8ro7y3r" data-mpi={index}>
                        <div className="text-2xl md:text-3xl font-bold mb-1" data-id="element-6305" data-mp="gxgunwrvm2r9">
                          {stat.value}
                        </div>
                        <div className="text-xs md:text-sm text-blue-200" data-id="element-6306" data-mp="bymdez9k4m99">
                          {stat.label}
                        </div>
                      </div>)}
                  </div>
                  <div className="flex flex-wrap justify-center md:justify-start gap-3 md:gap-4" data-id="element-6307" data-mp="ltxjyuatan5x">
                    {brandData.features.map((feature, index) => <div key={index} className="flex items-center gap-2 bg-white bg-opacity-20 px-3 md:px-4 py-2 rounded-full text-xs md:text-sm" data-id="element-6308" data-mp="19i466xcz809z" data-mpi={index}>
                        {feature.icon}
                        <span data-id="element-6309" data-mp="lu30dw76mxy9">{feature.text}</span>
                      </div>)}
                  </div>
                  <button onClick={handleFollow} className={`mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold transition-colors ${following ? 'bg-white/20 text-white border border-white/30' : 'bg-[#fdc300] text-[#1E2B55] hover:bg-[#ffd84d]'}`} data-id="element-6310" data-mp="qll2inkxz4kt">
                    <HeartIcon className={`w-5 h-5 ${following ? 'fill-white' : ''}`} data-id="element-6311" data-mp="2vgyif3ud2ta" />
                    {following ? 'Following' : 'Follow Brand'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Sticky Section Tabs — mobile only */}
          <div className="md:hidden sticky top-[56px] z-30 bg-white border-b border-gray-200 shadow-sm" data-id="element-6312" data-mp="11ewarvr6ze6p">
            <div className="overflow-x-auto scrollbar-hide px-4 py-2" data-id="element-6313" data-mp="wgbc40rl92kd">
              <div className="flex gap-2" data-id="element-6314" data-mp="we8kxdep93e2">
                {sectionTabs.map(tab => <button key={tab.id} onClick={() => handleTabClick(tab.id)} className={`whitespace-nowrap px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all ${activeTab === tab.id ? 'bg-[#1E2B55] text-white shadow-sm' : 'bg-gray-100 text-gray-700 active:bg-gray-200'}`} data-id="element-6315" data-mp="revmdvegu7vn">
                    {tab.label}
                  </button>)}
              </div>
            </div>
          </div>

          <PullToRefresh onRefresh={async () => {
          await new Promise(r => setTimeout(r, 1000));
        }} data-id="element-6316" data-mp="27vztmcm2p9ds">
            {/* Quick Actions */}
            <div className="py-2 md:py-8" data-id="element-6317" data-mp="2kc9bsoitcoxn">
              <BrandQuickActions brandSlug={brandSlug || ''} brandName={brandData.name} data-id="element-6318" data-mp="2p4ka5mxwo2o3" />
            </div>
            {/* Featured Products */}
            <div id="brand-section-featured" className="py-2 md:py-8 scroll-mt-32 transition-all" data-id="element-6319" data-mp="2u5giccpaiazb">
              <BrandFeaturedProducts brandSlug={brandSlug || ''} data-id="element-6320" data-mp="21g13fbui7xy4" />
            </div>
            {/* Shop by Category */}
            <div id="brand-section-categories" className="py-2 md:py-8 scroll-mt-32 transition-all" data-id="element-6321" data-mp="26chsc89j2gc9">
              <BrandShopByCategory brandSlug={brandSlug || ''} data-id="element-6322" data-mp="3ht82g2h2nsb5" />
            </div>
            {/* Special Offers */}
            <div id="brand-section-offers" className="py-2 md:py-8 scroll-mt-32 transition-all" data-id="element-6323" data-mp="3lua2glitc8e6">
              <BrandOffers brandSlug={brandSlug || ''} data-id="element-6324" data-mp="1moi6bdmxa0jm" />
            </div>
            {/* Top Rated */}
            <div id="brand-section-top-rated" className="py-2 md:py-8 scroll-mt-32 transition-all" data-id="element-6325" data-mp="1hqp0ilpj78jv">
              <BrandTopRated brandSlug={brandSlug || ''} data-id="element-6326" data-mp="1qrwmc4av2gx2" />
            </div>
            {/* Combo Sets */}
            <div id="brand-section-combos" className="py-2 md:py-8 scroll-mt-32 transition-all" data-id="element-6327" data-mp="131kh51702ya6">
              <BrandComboSets brandSlug={brandSlug || ''} data-id="element-6328" data-mp="z21xe8qx0t8x" />
            </div>
            {/* Brand Videos */}
            <div id="brand-section-videos" className="py-2 md:py-8 scroll-mt-32 transition-all" data-id="element-6329" data-mp="1ct6o2lcb2b74">
              <BrandVideos brandSlug={brandSlug || ''} data-id="element-6330" data-mp="kbb4exdl7o48" />
            </div>
            {/* About / Timeline */}
            <div id="brand-section-about" className="py-4 md:py-12 bg-white scroll-mt-32 transition-all" data-id="element-6331" data-mp="fd1asr7igvkq">
              <BrandTimeline brand={{
              name: brandData.name,
              since: brandData.since
            }} data-id="element-6332" data-mp="2qyjvs4id3rag" />
            </div>
          </PullToRefresh>
        </main>
        <Footer data-id="element-6333" data-mp="2my9f9xwy1ihc" />
        <MobileFooterNav data-id="element-6334" data-mp="2mxq91egnoij5" />
        <PharmacistChat data-id="element-6335" data-mp="2mzb0uwbl5ndb" />
        <div className="h-16 md:hidden" data-id="element-6336" data-mp="2myhs5pt2gnsj"></div>

        <LocationModal isOpen={locationModalOpen} onClose={() => setLocationModalOpen(false)} onSelectLocation={location => {
        setSelectedLocation(location);
        setLocationModalOpen(false);
      }} data-id="element-6337" data-mp="3anauc40a7rvl" />
      </div>
    </HelmetProvider>;
}